import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Search,
  ExternalLink,
  GitBranch,
  Video,
  FileText,
  Link2,
  Tag,
  Building2,
  Calendar,
  ChevronRight,
} from 'lucide-react';
import { showcaseService, ShowcaseEntry, ShowcaseStats } from '../services/showcase';

const INDUSTRIES = [
  'All Industries',
  'Financial Services',
  'Public Sector',
  'Healthcare',
  'Retail',
  'Logistics',
  'Manufacturing',
  'Technology',
  'Energy',
  'Media',
  'Professional Services',
];

const ASSET_ICONS: Record<string, React.ElementType> = {
  repo: GitBranch,
  video: Video,
  doc: FileText,
  other: Link2,
};

function AssetIcon({ type }: { type: string }) {
  const Icon = ASSET_ICONS[type] || Link2;
  return <Icon size={13} />;
}

function IndustryChip({ industry }: { industry?: string }) {
  if (!industry) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 uppercase tracking-wider">
      <Building2 size={10} />
      {industry}
    </span>
  );
}

function ShowcaseCard({ entry, onClick }: { entry: ShowcaseEntry; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="text-left bg-white border border-[#e2e8f0] rounded-xl p-5 flex flex-col gap-3 hover:shadow-md hover:border-primary/30 transition-all group"
    >
      <div className="flex items-start justify-between gap-2">
        <IndustryChip industry={entry.industry} />
        <span className="text-[10px] text-slate-400 flex items-center gap-1 flex-shrink-0">
          <Calendar size={10} />
          {entry.publishedAt ? new Date(entry.publishedAt).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : ''}
        </span>
      </div>

      <h3 className="text-sm font-bold text-navy leading-snug group-hover:text-primary transition-colors">
        {entry.challengeTitle || 'Untitled challenge'}
      </h3>

      {entry.challengeSummary && (
        <p className="text-xs text-slate-500 leading-relaxed line-clamp-3">
          {entry.challengeSummary}
        </p>
      )}

      {entry.tags && entry.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {entry.tags.slice(0, 4).map(tag => (
            <span key={tag} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
              {tag}
            </span>
          ))}
          {entry.tags.length > 4 && (
            <span className="text-[10px] text-slate-400">+{entry.tags.length - 4}</span>
          )}
        </div>
      )}

      {entry.assets && entry.assets.length > 0 && (
        <div className="flex items-center gap-1 text-[10px] text-slate-400 border-t border-slate-100 pt-2 mt-auto">
          <ExternalLink size={10} />
          {entry.assets.length} asset{entry.assets.length !== 1 ? 's' : ''}
        </div>
      )}

      <div className="flex items-center gap-1 text-[10px] text-primary font-semibold mt-auto">
        View details <ChevronRight size={11} />
      </div>
    </button>
  );
}

export default function Showcase() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<ShowcaseEntry[]>([]);
  const [stats, setStats] = useState<ShowcaseStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('All Industries');

  useEffect(() => {
    showcaseService.list()
      .then(({ entries: e, stats: s }) => {
        setEntries(e);
        setStats(s);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return entries.filter(e => {
      const matchesSearch = !q ||
        (e.challengeTitle || '').toLowerCase().includes(q) ||
        (e.challengeSummary || '').toLowerCase().includes(q) ||
        (e.tags || []).some(t => t.toLowerCase().includes(q));
      const matchesIndustry = selectedIndustry === 'All Industries' || e.industry === selectedIndustry;
      return matchesSearch && matchesIndustry;
    });
  }, [entries, search, selectedIndustry]);

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Sparkles size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Engagement Showcase</h1>
              <p className="text-slate-500 text-sm font-medium">Anonymised catalog of co-creation sessions</p>
            </div>
          </div>

          {/* Stat cards */}
          {stats && (
            <div className="flex items-center gap-3">
              {[
                { label: 'Sessions (30d)', value: stats.sessionsLast30Days },
                { label: 'Total sessions', value: stats.totalSessions },
                { label: 'Showcased', value: stats.totalPublished },
                { label: 'Industries', value: stats.distinctIndustries },
              ].map(({ label, value }) => (
                <div key={label} className="flex flex-col bg-white border border-primary/30 rounded-lg px-4 py-2 min-w-[100px] shadow-sm relative overflow-hidden group">
                  <div className="absolute inset-0 bg-primary/5 group-hover:bg-primary/10 transition-colors" />
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider z-10">{label}</span>
                  <span className="text-2xl font-bold text-navy z-10">{value}</span>
                </div>
              ))}
            </div>
          )}
        </header>

        {/* Search + filter + cards */}
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          {/* Filter bar */}
          <div className="p-4 border-b border-[#e2e8f0]">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by title, tag, or keyword…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-[#e2e8f0] rounded-xl text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
              </div>
              <select
                value={selectedIndustry}
                onChange={e => setSelectedIndustry(e.target.value)}
                className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
              >
                {INDUSTRIES.map(i => (
                  <option key={i}>{i}</option>
                ))}
              </select>
              {(search || selectedIndustry !== 'All Industries') && (
                <button
                  onClick={() => { setSearch(''); setSelectedIndustry('All Industries'); }}
                  className="text-xs text-slate-400 hover:text-slate-600 transition-colors px-2"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex justify-center items-center py-20 text-slate-400 text-sm">
              Loading showcase…
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-xl bg-white p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Sparkles size={28} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">
                {entries.length === 0 ? 'No showcase entries published yet.' : 'No entries match your search.'}
              </p>
              <p className="text-slate-400 text-xs mt-2">
                {entries.length === 0
                  ? 'Enable showcase on a session to get started.'
                  : 'Try a different search or clear the filters.'}
              </p>
            </div>
          ) : (
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(entry => (
                <ShowcaseCard
                  key={entry.showcaseId}
                  entry={entry}
                  onClick={() => navigate(`/showcase/${entry.showcaseId}`)}
                />
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
